import {Avatar, Modal} from 'antd'
import {getId} from 'apis/model/base'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useMemo, useState} from 'react'
import {NullAsDefault, renderElse, renderIf} from 'views/Shared'
import CRUD from 'views/Shared/enums/CRUD'
import {SlateRenderer} from 'views/SlateEditor'
import {convertFromString, isEmptyContent} from 'views/SlateEditor/functions'
import {renderCourseSubject} from 'views/Workspace/Course/functions/renderCourseSubject'
import {HeaderWithIconLine} from "../../Templates/items/ListHeader";
import {RiSubscript} from "react-icons/ri";
import {GoDotFill} from "react-icons/go";
import Translate from "../../../../modules/local/Translate";
import {TbListDetails} from "react-icons/tb";
import {BsInfoCircleFill} from "react-icons/bs";
import LoadingBlock from "../../../Loading/LoadingBlock";
import {ItemInformation} from "../../../../components/common/ItemInformation";

const renderSubjectItem = ({renderActions = NullAsDefault, handleAction = NullAsDefault} = {}) => (item, index) => {
    const {subject, number_of_sessions, number_of_credits, is_mandatory, passed_score, passed_attendance_percentage} = item ?? {}
    const {description, subject_idcode} = subject ?? {}

    return (
        <Translate key={index}>
            {(t) => (
                <div
                    onClick={() => handleAction(CRUD.READ, item)}
                    className="flex items-start gap-3 p-3 background rounded-xl border border-color-50 hover:border-primary-200 hover:shadow-sm cursor-pointer transition-shadow">
                    <div
                        className="flex items-center justify-center flex-shrink-0 font-bold text-xs text-primary-400"
                        style={{
                            width: 28, height: 28, minWidth: 28,
                            borderRadius: 6,
                            background: 'var(--color-primary-50)',
                        }}>
                        {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                            <span className="font-semibold text-sm text-color-000 leading-snug flex-1 min-w-0">
                                {getTitle(subject)}
                            </span>
                            {renderIf(
                                is_mandatory === 1,
                                <span
                                    className="flex-shrink-0 text-xs font-medium px-1.5 py-0.5 rounded-md"
                                    style={{background: 'var(--color-primary-50)', color: 'var(--color-primary)'}}>
                                    {t('mandatory')}
                                </span>
                            )}
                        </div>
                        {renderIf(
                            subject_idcode,
                            <span className="text-xs text-color-400 italic">{`#${subject_idcode}`}</span>
                        )}
                        {renderIf(
                            description,
                            <div className="text-xs text-color-400 mt-0.5 max-lines-2 leading-snug">
                                {description}
                            </div>
                        )}
                        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                            <span className="text-xs text-color-500 italic">
                                {`${number_of_sessions} ${t('sessions')}`}
                            </span>
                            {renderIf(
                                number_of_credits,
                                <span className="text-xs text-color-500 italic">
                                    {`${number_of_credits} ${t('credits')}`}
                                </span>
                            )}
                            {renderIf(
                                passed_score,
                                <span className="text-xs text-color-500 italic">
                                    {`${t('passed score')}: ${passed_score}`}
                                </span>
                            )}
                            {renderIf(
                                passed_attendance_percentage,
                                <span className="text-xs text-color-500 italic">
                                    {`${t('attendance')}: ${passed_attendance_percentage}%`}
                                </span>
                            )}
                            <div className="flex-1"/>
                            {renderActions(item)}
                        </div>
                    </div>
                </div>
            )}
        </Translate>
    )
}

const DetailModalContent = ({item}) => {
    const t = useTranslate()
    const {
        subject,
        number_of_sessions, about,
        passed_attendance_percentage,
        passed_score,
    } = item ?? {}

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
                <Avatar
                    size={50}
                    icon={<RiSubscript size={26} className="text-primary-500"/>}
                    className="flex flex-center bg-primary-50 rounded-lg 111-111"
                />
                <div className="flex flex-col">
                    <span className="font-bold text-lg text-color-000">
                        {getTitle(subject)}
                    </span>
                    <ItemInformation label="subject code" value={`#${subject?.subject_idcode}`} className="text-xs italic"/>
                </div>
            </div>
            {subject?.description && (
                <div className="ml-2 text-color-300 tracking-wider">
                    {subject?.description}
                </div>
            )}
            <div className="flex flex-col gap-2">
                <HeaderWithIconLine
                    icon={<BsInfoCircleFill size={14} className="text-blue-400 mr-1"/>}
                    title="subject information"
                    className="text-sm text-color-400"
                />
                <div className="ml-0 md:ml-1 flex flex-col flex-1 gap-1">
                    <ItemInformation
                        showIcon={true}
                        label="number of sessions"
                        value={`${number_of_sessions} ${t('sessions')}`}
                        className="text-xs italic"
                    />
                    {renderIf(
                        passed_score,
                        <ItemInformation
                            showIcon={true}
                            label="passed score"
                            value={passed_score}
                            className="text-xs italic"
                        />
                    )}
                    {renderIf(
                        passed_attendance_percentage,
                        <ItemInformation
                            showIcon={true}
                            label="passed attendance percentage"
                            value={`${passed_attendance_percentage} ${t('%')}`}
                            className="text-xs italic"
                        />
                    )}
                    {renderElse(
                        isEmptyContent(about),
                        <div className="flex flex-col">
                            <div className="space-x-1">
                                <GoDotFill size={10} className="text-color-400"/>
                                <span className="text-sm text-color-500 italic">
                              {`${t('introduce')} : `}
                          </span>
                            </div>
                            <SlateRenderer value={convertFromString(about)}/>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const CourseClassSubjects = ({
                                 item,
                                 renderList,
                                 detail = false,
                                 ...props
                             }) => {
    const t = useTranslate()

    const {edit} = item ?? {}

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const data = _.get(item, 'class_subjects', [])
    const groupedData = _.groupBy(data, 'group_name');
    const groupedDataKeysNotSorted = Object.keys(groupedData);
    // Lọc ra các phần tử null
    const nullElements = _.filter(groupedDataKeysNotSorted, element => element === null);
    // Lọc ra các phần tử không phải null
    const nonNullElements = _.filter(groupedDataKeysNotSorted, element => element !== null);
    // Sắp xếp các phần tử không phải null
    const sortedNonNullElements = _.sortBy(nonNullElements, [element => element.toString().toLowerCase()]);
    // Gộp các phần tử null với các phần tử đã sắp xếp
    const groupedDataKeys = [...nullElements, ...sortedNonNullElements];

    const onCancel = () => {
        setAction({})
    }

    const modal = useMemo(() => {
        const {subject} = action?.value ?? {}
        const id = getId(subject)
        let Content = NullAsDefault
        let Wrapper = ({children}) => children({item: {}})
        let params = {}
        let modal_params = {}

        switch (action?.type) {
            case CRUD.READ:
                modal_params = {
                    width: 600,
                    footer: undefined,
                    cancelText: t('close'),
                    okButtonProps: {
                        hidden: true,
                    },
                    cancelButtonProps: {
                        className: 'text-color-200 button-rounded-lg no-border',
                    },
                    title: (
                        <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                            {t('subjects')}
                        </div>
                    ),
                }
                params = {item: action?.value}
                Content = DetailModalContent
                // Wrapper = withProps({id})(FetchSubjectById)
                break
            default:
                break
        }

        return (
            <Modal
                footer={null}
                onCancel={onCancel}
                destroyOnHidden={true}
                open={action?.type}
                className="custom-modal"
                {...modal_params}>
                <Wrapper>
                    {({item, isLoading}) => {
                        if (!!isLoading) {
                            return <LoadingBlock/>
                        }
                        return (
                            <Content
                                {...params}
                                {...{item: action?.value, onCancel}}
                            />
                        )
                    }}
                </Wrapper>
            </Modal>
        )
    }, [item, action])

    const renderActions = useCallback(
        (item) => {
            return (
                <div
                    onClick={(e) => {
                        e.stopPropagation()
                        setAction({type: CRUD.READ, value: item})
                    }}
                    className="text-xs text-color-400 hover:text-color-100 italic cursor-pointer">
                    {`${t('view detail')}...`}
                </div>
            )
        },
        []
    )

    return (
        <React.Fragment>
            {_.isFunction(renderList) ? (
                renderList({
                    edit,
                    action,
                    detail,
                    renderItem: renderCourseSubject({
                        detail,
                        renderActions,
                    }),
                })
            ) : (
                <div className="flex flex-col items-start">
                    <div className="w-full flex flex-col flex-1">
                        <div className="flex flex-col gap-4">
                            {groupedDataKeys.map((e, i) => {
                                return (
                                    <div key={i} className="w-full flex flex-col flex-1 gap-3">
                                        {renderIf(
                                            e !== 'null' && groupedDataKeys.length > 1,
                                            <div
                                                style={{
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    background: 'linear-gradient(to right, var(--background-200), rgba(0, 0, 0, 0) 100%)'
                                                }}
                                                className="w-90 flex items-center gap-1">
                                                <TbListDetails className="text-color-300"/>
                                                <span className="font-semibold text-sm text-color-300 uppercase tracking-wider">
                                                    {`  -  ${e}`}
                                                </span>
                                            </div>
                                        )}
                                        <div style={{marginLeft: 4}} className="space-y-4">
                                            {_.sortBy(_.get(groupedData, e, []), ['subject.title']).map((ee, ii) => {
                                                return renderSubjectItem({
                                                    renderActions,
                                                    handleAction: (type, value) => {
                                                        setAction({type, value});
                                                    },
                                                })(ee, ii)
                                            })}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            )}
            {modal}
        </React.Fragment>
    )
}

export default CourseClassSubjects
